import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { MKT_LABELS } from '../lib/FinanceFilters.js';
import { useNicknames } from '../lib/useNicknames.js';

// ── Types ────────────────────────────────────────────────────────────────────

interface CogsRow {
  sku: string;
  marketplace: string;
  nickname: string | null;
  batchCount: number;
  unitsRemaining: number;
  unitsOrdered: number;
  unitsDepleted: number;
  fifoCogs: number | null;
  weightedAvgCogs: number | null;
  oldestBatchDate: string | null;
  totalInvested: number;
  source: 'fifo' | 'manual';
  recentDepletions: number;
  lastDepletion: string | null;
}

interface Summary {
  totalInvested: number;
  totalRemaining: number;
  skusWithCogs: number;
  skusNoCogs: number;
  totalSkus: number;
}

interface CogsResponse {
  skus: CogsRow[];
  summary: Summary;
}

// ── Component ────────────────────────────────────────────────────────────────

export function FinanceCosts() {
  const { displayName, parentNickname } = useNicknames();
  const [data, setData] = useState<CogsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterMarket, setFilterMarket] = useState('all');
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' }>({ key: 'sku', dir: 'asc' });

  useEffect(() => {
    api.get<CogsResponse>('/finance/cogs-overview')
      .then(setData)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSkeleton />;
  if (error) return <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">{error}</div>;
  if (!data) return null;

  const { summary } = data;
  const markets = [...new Set(data.skus.map((r) => r.marketplace))].sort();
  const filtered = filterMarket === 'all' ? data.skus : data.skus.filter((r) => r.marketplace === filterMarket);

  // Sort
  const sorted = [...filtered].sort((a, b) => {
    const dir = sort.dir === 'asc' ? 1 : -1;
    switch (sort.key) {
      case 'sku': return dir * (a.sku).localeCompare(b.sku);
      case 'marketplace': return dir * ((MKT_LABELS[a.marketplace] ?? a.marketplace).localeCompare(MKT_LABELS[b.marketplace] ?? b.marketplace));
      case 'fifoCogs': return dir * ((a.fifoCogs ?? 0) - (b.fifoCogs ?? 0));
      case 'weightedAvgCogs': return dir * ((a.weightedAvgCogs ?? 0) - (b.weightedAvgCogs ?? 0));
      case 'unitsRemaining': return dir * (a.unitsRemaining - b.unitsRemaining);
      case 'totalInvested': return dir * (a.totalInvested - b.totalInvested);
      default: return 0;
    }
  });

  function toggleSort(key: string) {
    setSort((prev) => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' });
  }

  const sortIcon = (key: string) => sort.key === key ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : '';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold">COGS Overview <span className="text-xs font-normal text-gray-400 ml-2">All amounts in USD</span></h2>
          <p className="text-sm text-gray-500">FIFO landed costs from purchase batches — the actual COGS used in P&L calculations.</p>
        </div>
        <Link
          to="/inventory/batches"
          className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
        >
          Manage Batches
        </Link>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryCard label="Total Invested" value={`$${fmt(summary.totalInvested)}`} sub="across all batches" />
        <SummaryCard label="Units Remaining" value={fmt(summary.totalRemaining, 0)} sub="in active batches" />
        <SummaryCard
          label="COGS Coverage"
          value={`${summary.skusWithCogs} / ${summary.totalSkus}`}
          sub={summary.skusNoCogs > 0 ? `${summary.skusNoCogs} SKU${summary.skusNoCogs > 1 ? 's' : ''} missing COGS` : 'all SKUs have COGS'}
          alert={summary.skusNoCogs > 0}
        />
        <SummaryCard label="SKU × Market" value={String(data.skus.length)} sub="unique COGS entries" />
      </div>

      {/* Marketplace Filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-medium text-gray-500">Filter:</span>
        <button
          onClick={() => setFilterMarket('all')}
          className={`rounded-full px-3 py-1 text-xs font-medium transition ${filterMarket === 'all' ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
        >
          All
        </button>
        {markets.map((m) => (
          <button
            key={m}
            onClick={() => setFilterMarket(m)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition ${filterMarket === m ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            {MKT_LABELS[m] ?? m}
          </button>
        ))}
      </div>

      {/* COGS Table */}
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <h3 className="text-sm font-semibold mb-4">
          Per-SKU COGS ({sorted.length})
        </h3>

        {sorted.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-sm font-medium text-gray-900">No COGS data</p>
            <p className="mt-1 text-xs text-gray-500">
              <Link to="/inventory/batches" className="text-brand-600 hover:underline">Create purchase batches</Link> to track landed costs.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <Th label="SKU" sortKey="sku" onClick={toggleSort} icon={sortIcon} />
                  <Th label="Market" sortKey="marketplace" onClick={toggleSort} icon={sortIcon} />
                  <Th label="FIFO COGS" sortKey="fifoCogs" onClick={toggleSort} icon={sortIcon} align="right" />
                  <Th label="Wtd Avg" sortKey="weightedAvgCogs" onClick={toggleSort} icon={sortIcon} align="right" />
                  <Th label="Remaining" sortKey="unitsRemaining" onClick={toggleSort} icon={sortIcon} align="right" />
                  <th className="pb-2 text-right text-xs font-medium text-gray-500">Batches</th>
                  <Th label="Invested" sortKey="totalInvested" onClick={toggleSort} icon={sortIcon} align="right" />
                  <th className="pb-2 text-left text-xs font-medium text-gray-500">Source</th>
                  <th className="pb-2 text-left text-xs font-medium text-gray-500">Last Depletion</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {sorted.map((row) => (
                  <tr key={`${row.sku}-${row.marketplace}`} className="hover:bg-gray-50/50">
                    <td className="py-2.5" title={row.sku}>
                      {parentNickname(row.sku) && <div className="text-[10px] text-gray-400">{parentNickname(row.sku)}</div>}
                      <div className="font-medium text-gray-900">{displayName(row.sku)}</div>
                    </td>
                    <td className="py-2.5">
                      <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                        {MKT_LABELS[row.marketplace] ?? row.marketplace}
                      </span>
                    </td>
                    <td className="py-2.5 text-right font-mono text-gray-700">
                      {row.fifoCogs != null ? `$${row.fifoCogs.toFixed(2)}` : <span className="text-red-400">--</span>}
                    </td>
                    <td className="py-2.5 text-right font-mono text-gray-500">
                      {row.weightedAvgCogs != null ? `$${row.weightedAvgCogs.toFixed(2)}` : '--'}
                    </td>
                    <td className="py-2.5 text-right font-mono">
                      <span className={row.unitsRemaining <= 0 ? 'text-red-500 font-semibold' : 'text-gray-700'}>
                        {row.unitsRemaining}
                      </span>
                    </td>
                    <td className="py-2.5 text-right text-gray-500">{row.batchCount}</td>
                    <td className="py-2.5 text-right font-mono text-gray-700">
                      {row.totalInvested > 0 ? `$${fmt(row.totalInvested)}` : '--'}
                    </td>
                    <td className="py-2.5">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${
                        row.source === 'fifo'
                          ? 'bg-green-50 text-green-700'
                          : 'bg-yellow-50 text-yellow-700'
                      }`}>
                        {row.source === 'fifo' ? 'FIFO' : 'Manual'}
                      </span>
                    </td>
                    <td className="py-2.5 text-xs text-gray-400">
                      {row.lastDepletion ? new Date(row.lastDepletion).toLocaleDateString() : '--'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
        <p className="text-xs text-gray-500">
          <strong>FIFO COGS</strong> = landed unit cost of the oldest active batch (what P&L uses).{' '}
          <strong>Wtd Avg</strong> = weighted average across all remaining batches.{' '}
          <strong>Source</strong>: FIFO = from purchase batches, Manual = fallback from product_costs table.{' '}
          <Link to="/inventory/batches" className="text-brand-600 hover:underline">Manage batches</Link> to update costs.
        </p>
      </div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(n: number, decimals = 2) {
  return n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function SummaryCard({ label, value, sub, alert }: { label: string; value: string; sub: string; alert?: boolean }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className="mt-1 text-xl font-bold text-gray-900">{value}</p>
      <p className={`mt-0.5 text-xs ${alert ? 'text-yellow-600 font-medium' : 'text-gray-400'}`}>{sub}</p>
    </div>
  );
}

function Th({ label, sortKey, onClick, icon, align }: {
  label: string; sortKey: string;
  onClick: (k: string) => void; icon: (k: string) => string; align?: string;
}) {
  return (
    <th
      className={`pb-2 text-xs font-medium text-gray-500 cursor-pointer hover:text-gray-700 select-none ${align === 'right' ? 'text-right' : 'text-left'}`}
      onClick={() => onClick(sortKey)}
    >
      {label}{icon(sortKey)}
    </th>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="flex items-center justify-between">
        <div>
          <div className="h-5 w-40 rounded bg-gray-200" />
          <div className="mt-2 h-4 w-64 rounded bg-gray-200" />
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => <div key={i} className="h-24 rounded-xl bg-gray-200" />)}
      </div>
      <div className="h-64 rounded-xl bg-gray-200" />
    </div>
  );
}
